import { useMemo } from 'react'

import { useMutation, useQueryClient } from 'react-query'

import { teamService } from '@/modules/team/service/team-service.ts'

import type {
  TeamCreateRoleRequest,
  TeamRole,
  TeamRolePermissionAssignmentRequest,
} from '../types'

export const useUpdateTeamRoleTable = () => {
  const queryClient = useQueryClient()

  const deleteMutation = useMutation(
    (id: number) => teamService.deleteTeamRole(id),
    {
      onMutate: (roleId) => {
        queryClient.setQueryData<TeamRole[] | undefined>(
          ['team', 'roles'],
          (prevRoles) => {
            return prevRoles?.filter((role) => role.id !== roleId)
          },
        )
      },
      onSettled: () => {
        queryClient.invalidateQueries(['team', 'roles'])
      },
    },
  )

  const createRoleMutation = useMutation(
    (newRoleDetails: TeamCreateRoleRequest) =>
      teamService.createNewRole(newRoleDetails),
    {
      onSettled: () => {
        queryClient.invalidateQueries(['team', 'roles'])
      },
    },
  )

  const editMutation = useMutation(
    ({
      newRoleDetails,
      roleId,
    }: {
      newRoleDetails: Partial<TeamCreateRoleRequest>
      roleId: number
    }) => teamService.editRole(newRoleDetails, roleId),
    {
      onSettled: () => {
        queryClient.invalidateQueries(['team', 'roles'])
      },
    },
  )

  const assignPermissionsMutation = useMutation(
    ({
      newPermissionDetails,
      roleId,
    }: {
      newPermissionDetails: TeamRolePermissionAssignmentRequest
      roleId: number
    }) => teamService.assignPermissionsToRole(newPermissionDetails, roleId),
    {
      onSettled: () => {
        queryClient.invalidateQueries(['team', 'roles'])
      },
    },
  )

  const assignRolesToMemberMutation = useMutation(
    ({ roleIds, memberId }: { roleIds: number[]; memberId: number }) =>
      teamService.assignRolesToMember(memberId, roleIds),
    {
      onSettled: () => {
        // TODO: Invalidate the team member query to refetch the data
        queryClient.invalidateQueries(['member'])
      },
    },
  )

  return useMemo(
    () => ({
      deleteRole: deleteMutation,
      editRole: editMutation,
      createRole: createRoleMutation,
      assignPermissions: assignPermissionsMutation,
      assignRolesToMember: assignRolesToMemberMutation,
    }),
    [
      assignPermissionsMutation,
      createRoleMutation,
      deleteMutation,
      editMutation,
      assignRolesToMemberMutation,
    ],
  )
}
